import React, { forwardRef, useImperativeHandle } from "react";

import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import IconButton from "@mui/material/IconButton";
import { Box, Typography, useMediaQuery } from "@mui/material";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import PeopleIcon from "@mui/icons-material/People";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderIcon from "@mui/icons-material/Folder";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import IosShareIcon from "@mui/icons-material/IosShare";
import Add from "@mui/icons-material/Add";
import Audiotrack from "@mui/icons-material/Audiotrack";
import FormatUnderlined from "@mui/icons-material/Subtitles";
import TextIcon from "@mui/icons-material/TextFields";
import SourceIcon from "@mui/icons-material/Source";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import FilterIcon from "@mui/icons-material/Tune";

import SidebarList from "./components/SidebarList";
import MobileSidebar from "./components/MobileSidebar";
import { SidebarListProps } from "./types";
import { SidebarContent } from "./components/SidebarContent";
import { RoleName } from "../enums/RoleName";
import RoleBasedAccess from "../RoleBasedAccess";

const COLLAPSED_WIDTH = 80;
const EXPANDED_WIDTH = 300;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    backgroundColor: "#ffffff",
    border: "1px solid #e9ecef",
    borderRight: "2px solid #dee2e6",
    position: "relative",
    whiteSpace: "nowrap",
    width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    overflowX: "hidden",
    height: "100vh",
  },
}));

interface SidebarProps {
  pageName: string;
  expended?: boolean;
  editFileSidebar?: boolean;
  hidePrimary?: boolean;
}

export interface SidebarRef {
  toggleMobile: () => void;
}

const SidebarView = forwardRef<SidebarRef, SidebarProps>(
  (
    { pageName, expended = true, editFileSidebar = false, hidePrimary = false },
    ref,
  ) => {
    const globalTheme = useTheme();
    const mobile = useMediaQuery(globalTheme.breakpoints.down("md"));
    const newExpendedState = mobile ? false : expended;

    // Initialize state from session storage or default value
    const [open, setOpen] = React.useState(() => {
      if (mobile) return false;
      const savedState = sessionStorage.getItem("sidebarOpen");
      return savedState !== null ? JSON.parse(savedState) : newExpendedState;
    });

    const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

    useImperativeHandle(ref, () => ({
      toggleMobile: () => setMobileSidebarOpen(!mobileSidebarOpen),
    }));

    // Save sidebar state to session storage whenever it changes
    React.useEffect(() => {
      if (!mobile) {
        sessionStorage.setItem("sidebarOpen", JSON.stringify(open));
      }
    }, [open, mobile]);

    const handleToggleSidebar = () => {
      setOpen(!open);
    };

    const handleMobileToggle = () => {
      setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const sidebarList: SidebarListProps[] = [
      {
        icon: <PlaylistPlayIcon />,
        name: "Episodes",
        link: "/episodes",
        isSelected: pageName === "Episodes",
      },
      {
        icon: <CloudUploadIcon />,
        name: "Upload Episode",
        link: "/episodes/upload",
        isSelected: pageName === "Upload Episode",
      },
      {
        icon: <FolderIcon />,
        name: "Projects",
        link: "/projects",
        isSelected: pageName === "Projects",
      },
      {
        icon: <FolderSharedIcon />,
        name: "Shared Episodes",
        link: "/shared-episodes",
        disabled: true,
        isSelected: false,
        subMenu: [
          {
            icon: <IosShareIcon />,
            name: "Shared Episodes",
            link: "/shared-episodes",
            isSelected: pageName === "Shared Episodes",
          },
          {
            icon: <CoPresentIcon />,
            name: "Episodes Shared With Me",
            link: "/episodes-shared-with-me",
            isSelected: pageName === "Episodes Shared With Me",
          },
        ],
      },
    ];

    const sidebarList2 = [
      {
        icon: <Add sx={{ width: "100%" }} />,
        name: "Media",
        disabled: true,
      },
      {
        icon: <Audiotrack sx={{ width: "100%" }} />,
        name: "Audio",
        disabled: true,
      },
      {
        icon: <FormatUnderlined sx={{ width: "100%" }} />,
        name: "Captions",
      },
      {
        icon: <TextIcon sx={{ width: "100%" }} />,
        name: "Text",
        disabled: true,
      },
      {
        icon: <SourceIcon sx={{ width: "100%" }} />,
        name: "Elements",
        disabled: true,
      },
      {
        icon: <LinearScaleIcon sx={{ width: "100%" }} />,
        name: "Transitions",
        disabled: true,
      },
      {
        icon: <FilterIcon sx={{ width: "100%" }} />,
        name: "Filters",
        disabled: true,
      },
    ];

    const sidebarBottomList: SidebarListProps[] = [
      {
        icon: <PeopleIcon />,
        name: "Users",
        link: "/users",
        isSelected: pageName === "Users",
      },
    ];

    // If mobile, render the mobile sidebar
    if (mobile) {
      return (
        <>
          {/* Mobile logo button - always visible */}
          <Box
            sx={{
              position: "fixed",
              top: { xs: 8, sm: 12 },
              left: { xs: 8, sm: 12 },
              zIndex: 1200,
              display: { xs: "block", md: "none" },
            }}
          >
            <IconButton
              onClick={handleMobileToggle}
              sx={{
                color: "#1976d2",
                backgroundColor: "#ffffff",
                border: "1px solid #e9ecef",
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "#f5f7fa",
                  color: "#1565c0",
                },
              }}
            >
              <img
                src="/logo.png"
                alt="Podcast Assistant Logo"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                }}
              />
            </IconButton>
          </Box>

          {/* Mobile sidebar drawer */}
          <MobileSidebar
            open={mobileSidebarOpen}
            onClose={() => setMobileSidebarOpen(false)}
            pageName={pageName}
          />
        </>
      );
    }

    // Desktop sidebar
    return (
      <>
        <Drawer
          hidden={hidePrimary}
          variant="permanent"
          open={open}
          sx={{
            position: "relative",
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              px: 3,
              minHeight: 80,
              backgroundColor: "#ffffff",
              borderBottom: "1px solid #e9ecef",
              borderTop: "1px solid #e9ecef",
              borderLeft: "1px solid #e9ecef",
              justifyContent: open ? "space-between" : "center",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            {open ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 2,
                  ml: 0.5,
                }}
              >
                <img
                  src="/logo.png"
                  alt="Podcast Assistant Logo"
                  style={{ width: 36, height: 36, display: "block" }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                    color: "#23272E",
                    lineHeight: 1.3,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    letterSpacing: "0.02em",
                    mt: 0.5,
                  }}
                >
                  Podcast Assistant
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  cursor: "pointer",
                }}
                onClick={() => setOpen(true)}
              >
                <img
                  src="/logo.png"
                  alt="Podcast Assistant Logo"
                  style={{ width: 36, height: 36, display: "block" }}
                />
              </Box>
            )}
            {open && (
              <IconButton
                onClick={handleToggleSidebar}
                sx={{
                  color: "#666",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e9ecef",
                  width: 40,
                  height: 40,
                  ml: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#f5f7fa",
                    color: "#1976d2",
                    borderColor: "#adb5bd",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
          </Toolbar>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <SidebarList sidebarList={sidebarList} open={open} mobile={false} />
          </Box>
          <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                width: "100%",
                backgroundColor: "#ffffff",
                borderTop: "1px solid #e9ecef",
                borderLeft: "1px solid #e9ecef",
                borderRight: "1px solid #e9ecef",
                boxShadow: "0 -2px 4px rgba(0,0,0,0.05)",
              }}
            >
              <SidebarList
                sidebarList={sidebarBottomList}
                open={open}
                mobile={false}
              />
            </Box>
          </RoleBasedAccess>
        </Drawer>
        {editFileSidebar ? (
          <Box sx={{ position: "relative" }}>
            <SidebarContent
              sidebarContent={sidebarList2 as SidebarListProps[]}
            />
          </Box>
        ) : null}
      </>
    );
  },
);

export default SidebarView;
